"use client";

import type { ComponentPropsWithoutRef, ReactNode } from "react";

import { Button } from "@components/core/Button";
import { Noise } from "@ui/components/core/Noise/Noise";
import { cva } from "class-variance-authority";
import { clsx } from "clsx";

import { DiscBackground } from "./helpers";
import type { DiscColors, DiscImageConfig } from "./helpers";

export type DiscBaseProps = {
  className?: string;
  /** Background image configuration */
  image?: DiscImageConfig;
  /** Gradient colors (used when no image is provided and showGradient is true) */
  colors?: DiscColors;
  /** Show gradient background when no image (default: true). Set to false for solid background. */
  showGradient?: boolean;
  /** Center content (use DiscCenterButton for standard button appearance) */
  children?: ReactNode;
  /** Noise overlay opacity (0-1), default 0.5 */
  noiseOpacity?: number;
  /** Show elevated shadow on the disc */
  showShadow?: boolean;
  /** Animate background transitions */
  animate?: boolean;
  /** Callback when the disc is clicked (makes entire disc clickable) */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
} & ComponentPropsWithoutRef<"button">;

export const ELEVATED_SHADOW = "tw-shadow-[0_0_1px_0_rgba(0,0,0,0.4),0_2px_4px_rgba(0,0,0,0.04)]";

export const DEFAULT_COLORS: DiscColors = {
  primary: "#3B9FE8",
  secondary: "#F08B6D",
  accent: "#B88ED6",
  highlight: "#F5C28D",
};

const discCenterButtonVariants = cva(
  "tw-rounded-3xl tw-bg-cream tw-text-black tw-backdrop-blur-[10px] tw-flex tw-items-center tw-justify-center",
  {
    variants: {
      size: {
        base: "tw-size-9 sm:tw-size-11",
        responsive: "tw-size-9 sm:tw-size-11",
      },
      showShadow: {
        true: ELEVATED_SHADOW,
        false: "",
      },
    },
    defaultVariants: {
      size: "base",
      showShadow: false,
    },
  },
);

export interface DiscCenterButtonProps {
  icon: ReactNode;
  onClick?: () => void;
  showShadow?: boolean;
  className?: string;
}

export function DiscCenterButton({ icon, onClick, showShadow = false, className }: DiscCenterButtonProps) {
  const sharedClassName = clsx(discCenterButtonVariants({ showShadow }), className);

  if (onClick) {
    return <Button icon={icon} size='pill-sm' variant='ghost' onClick={onClick} className={sharedClassName} />;
  }

  return <div className={sharedClassName}>{icon}</div>;
}

export function DiscBase({
  className,
  image,
  colors = DEFAULT_COLORS,
  showGradient = true,
  children,
  noiseOpacity = 0.5,
  showShadow = true,
  animate = false,
  onClick,
  ...rest
}: DiscBaseProps) {
  const sharedClassName = clsx(
    "tw-relative tw-aspect-square tw-h-full tw-w-full tw-overflow-hidden tw-rounded-full tw-ring-[0.5px] tw-ring-inset tw-ring-black/10",
    showShadow && ELEVATED_SHADOW,
    className,
  );

  const content = (
    <>
      <DiscBackground image={image} colors={colors} showGradient={showGradient} animate={animate} />
      <Noise style={{ opacity: noiseOpacity }} />
      {children && (
        <div
          className={clsx(
            "tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center",
            onClick && "tw-opacity-100 tw-transition-opacity group-hover:tw-opacity-90",
          )}
        >
          {children}
        </div>
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type='button'
        className={clsx(
          sharedClassName,
          "tw-group tw-cursor-pointer tw-outline-none focus-visible:tw-outline-focus tw-rounded-full disabled:tw-cursor-not-allowed",
        )}
        onClick={onClick}
        {...rest}
      >
        {content}
      </button>
    );
  }

  return <div className={sharedClassName}>{content}</div>;
}
